// F6 provenance. Every clipboard copy and every rendered generated document
// carries one footer: when CensusZen drafted the text, who signed the review
// off (or that nobody has yet), and that the source records still govern.
// Plain hyphens only; the physician query builder strips dashes and the
// house writing rules forbid the long ones.

const UNKNOWN_DATE = 'an unknown date';

function toDate(value) {
    if (!value) return null;
    if (typeof value.toDate === 'function') {
        try {
            return value.toDate();
        } catch {
            return null;
        }
    }
    if (value instanceof Date) return value;
    // A Firestore Timestamp that crossed a plain-object boundary keeps its
    // seconds field but loses toDate().
    if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
    if (typeof value === 'number') return new Date(value);
    if (typeof value === 'string') return new Date(value);
    return null;
}

/**
 * A Firestore Timestamp, a Date, an ISO string, or null, as the viewer's
 * locale date. Anything unparseable reads as "an unknown date".
 */
export function formatProvenanceDate(value) {
    const date = toDate(value);
    if (!date || Number.isNaN(date.getTime())) return UNKNOWN_DATE;
    return date.toLocaleDateString();
}

/**
 * The footer sentence, signed or pending.
 * @param {{generatedAt?: *, signoff?: {name?: string, at?: *}}} meta
 */
export function provenanceFooter({ generatedAt, signoff } = {}) {
    const first = `AI-assisted draft generated by CensusZen on ${formatProvenanceDate(generatedAt)}.`;
    const middle = signoff?.name
        ? `Reviewed by ${signoff.name} on ${formatProvenanceDate(signoff.at)}.`
        : 'Review pending.';
    return `${first} ${middle} Verify against source records before use.`;
}

function copyByExecCommand(payload) {
    const textArea = document.createElement('textarea');
    textArea.value = payload;
    document.body.appendChild(textArea);
    textArea.select();
    try {
        return document.execCommand('copy');
    } finally {
        document.body.removeChild(textArea);
    }
}

/**
 * Copy text plus its provenance footer. Resolves true on success, false on
 * failure, so callers can drive their own "Copied!" state.
 */
export async function copyWithProvenance(text, meta) {
    if (!text) return false;
    const payload = `${text}\n\n${provenanceFooter(meta)}`;
    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(payload);
            return true;
        }
        return copyByExecCommand(payload) !== false;
    } catch (error) {
        console.error('Copy failed:', error);
        return false;
    }
}
